import { CircleCheck, Clock, Pencil, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useReminderCancelSnoozeMutation } from "web_myplann/Api/Reminders/useReminderCancelSnoozeMutation";
import { useReminderResolveMutation } from "web_myplann/Api/Reminders/useReminderResolveMutation";
import { ReminderStatusEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { Reminder } from "web_myplann/Reminders/Types/ReminderTypes";
import { ActionsDrawerRedesign } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRedesign";
import type { DrawerAction } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRow";
import { parseApiDate } from "web_myplann/Shared/Utils/apiDate";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";

type Props = {
	reminder: Reminder;
	open: boolean;
	onEdit: () => void;
	onSnooze: () => void;
	onDelete: () => void;
	onClose: () => void;
};

export const ReminderActionsDrawer = ({
	reminder,
	open,
	onEdit,
	onSnooze,
	onDelete,
	onClose,
}: Props) => {
	const { t } = useResource();

	const { mutate: resolveReminder } = useReminderResolveMutation();
	const { mutate: cancelSnooze } = useReminderCancelSnoozeMutation();

	const isSnoozed = reminder.status === ReminderStatusEnum.Snoozed;

	const handleResolve = () => {
		resolveReminder(reminder.id, {
			onSuccess: ({ nextOccurrence }) => {
				toast.success(
					nextOccurrence
						? t(Resources.Reminders.Resolve.nextScheduledToast, {
								name: nextOccurrence.title,
								date: dateFormatter(parseApiDate(nextOccurrence.date)),
							})
						: t(Resources.Reminders.Resolve.resolvedToast),
				);
				onClose();
			},
		});
	};

	const handleCancelSnooze = () => {
		cancelSnooze(reminder.id, { onSuccess: onClose });
	};

	const stateAction: DrawerAction = isSnoozed
		? {
				key: "cancelSnooze",
				label: t(Resources.Reminders.Actions.cancelSnooze),
				icon: Undo2,
				onClick: handleCancelSnooze,
			}
		: {
				key: "snooze",
				label: t(Resources.Reminders.Actions.snooze),
				icon: Clock,
				onClick: onSnooze,
			};

	const actions: DrawerAction[] = [
		{
			key: "resolve",
			label: t(Resources.Reminders.Actions.resolve),
			icon: CircleCheck,
			onClick: handleResolve,
		},
		stateAction,
		{
			key: "edit",
			label: t(Resources.Reminders.Actions.edit),
			icon: Pencil,
			onClick: onEdit,
		},
		{
			key: "delete",
			label: t(Resources.Reminders.Actions.remove),
			icon: Trash2,
			onClick: onDelete,
			isDestructive: true,
		},
	];

	return (
		<ActionsDrawerRedesign
			open={open}
			onClose={onClose}
			title={reminder.name}
			actions={actions}
			variant="plain"
		/>
	);
};
